import Cookies from 'js-cookie';

import { R } from '@genially/commons';
import type { SnowplowCookieHandler } from '@genially/snowplow';

import config from '../config/config';
import type {
  Optanon,
  OptanonCookieInfo,
  OptanonDomainDataGroup,
} from '../types/globals';

const { filter, pipe, map, chain, whereEq, forEach } = R;

type PropEq = (a: string, b: string) => (o: Record<string, string>) => boolean;

const propEq: PropEq = (propName, propValue) => object => object[propName] === propValue;

export const enum OptanonCustomGroupId {
  Necessary = 'C0001',
  Performance = 'C0002',
  Functional = 'C0003',
  Targeting = 'C0004',
  SocialMedia = 'C0005',
}

const COOKIES_BY_GROUPS: { cookie: string; group: string }[] = [
  { cookie: 'Google Analytics', group: OptanonCustomGroupId.Performance.toString() },
  { cookie: 'Snowplow', group: OptanonCustomGroupId.Performance.toString() },
];

type OptanonDomainDataGroupFilter = (
  group: OptanonDomainDataGroup[],
) => OptanonDomainDataGroup[];
const filterByCookieGroupId = (
  groupId: OptanonCustomGroupId,
): OptanonDomainDataGroupFilter =>
  filter<OptanonDomainDataGroup>(whereEq({ CustomGroupId: groupId }));

const getCookiesFromGroup = (group: OptanonDomainDataGroup): OptanonCookieInfo[] =>
  group.Cookies;

const getCookieNames = map((cookie: OptanonCookieInfo) => cookie.Name);

class CookiePermissions implements SnowplowCookieHandler {
  private readonly window = window;

  private readonly debug: boolean;

  constructor(debug = false) {
    this.debug = debug;
  }

  protected get optanon() {
    return this.window.Optanon;
  }

  protected get onetrustActiveGroups() {
    return this.window.OnetrustActiveGroups || '';
  }

  // eslint-disable-next-line class-methods-use-this
  private hasCookiesManager = (): this is {
    optanon: Optanon;
  } => !!window.Optanon;

  // eslint-disable-next-line class-methods-use-this
  private getGroupFromCookie = (cookieName: string): string | null => {
    const cookie = COOKIES_BY_GROUPS.find(propEq('cookie', cookieName));

    return cookie ? cookie.group : null;
  };

  private groupHasPermissions = (group: string): boolean =>
    this.onetrustActiveGroups.split(',').filter(Boolean).includes(group);

  public triggerModal = (): void => {
    if (this.debug || !this.hasCookiesManager()) {
      return;
    }

    this.optanon.ToggleInfoDisplay();
  };

  public checkPermission = (cookie: string): boolean | Error => {
    if (this.debug) {
      return true;
    }

    if (!this.hasCookiesManager()) {
      return false;
    }

    const group = this.getGroupFromCookie(cookie);

    if (!group) {
      throw new Error(`The cookie "${cookie}" is not grouped in our dictionary`);
    }

    return this.groupHasPermissions(group);
  };

  public areSnowplowCookiesEnabled(): boolean | Error {
    return this.checkPermission('Snowplow');
  }

  public removeTargetingCookies(): void {
    this.removeCookies(OptanonCustomGroupId.Targeting);
  }

  public removeSocialMediaCookies(): void {
    this.removeCookies(OptanonCustomGroupId.SocialMedia);
  }

  // Based on OneTrust documentation: https://community.cookiepro.com/s/article/UUID-b2d72003-853b-e75b-e337-c6d26ea327eb
  private removeCookies(groupId: OptanonCustomGroupId): void {
    if (!this.hasCookiesManager()) {
      return;
    }

    const removeCookiesByName = forEach((name: string): void => {
      Cookies.remove(name, { path: '/', domain: '.genially.com' });
      Cookies.remove(name, { path: '/', domain: window.location.hostname });
    });

    const getCookieNamesToRemove = pipe(
      filterByCookieGroupId(groupId),
      chain(getCookiesFromGroup),
      getCookieNames,
    );

    const cookieNamesToRemove = getCookieNamesToRemove(
      this.optanon.GetDomainData().Groups,
    );
    removeCookiesByName(cookieNamesToRemove);
  }
}

const cookiePermissions = new CookiePermissions(config.debug);
export { cookiePermissions, CookiePermissions };
